import { ApolloError } from '@apollo/client';
import { Button, ButtonProps } from '@apollo/orbit';
import * as Sentry from '@sentry/react';
import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import * as orgRoutes from 'src/app/account/routes';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { EventCategory } from 'src/lib/analytics';
import Config from 'src/lib/config';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { useDemoGraphContext } from './demoGraphContext/DemoGraphContext';

interface Props {
  onClick?: React.MouseEventHandler<HTMLElement> | undefined;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  'data-analytics-label'?: string;
  'data-analytics-category'?: EventCategory;
  className?: string;
  btnLabel?: React.ReactNode;
  role?: string;
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
}

export const TryDemoGraphButton = ({
  onClick,
  onMouseEnter,
  onMouseLeave,
  btnLabel = 'Try a Demo Graph',
  size = 'lg',
  ...rest
}: Props) => {
  const history = useHistory();
  const trackCustomEvent = useTrackCustomEvent();
  const {
    createDemoSupergraph,
    orgId,
    graphVariant,
    isProcessing,
    hasSchemaDocs,
  } = useDemoGraphContext();

  const createGraph = useCallback(async () => {
    try {
      const createSupergraphResult = await createDemoSupergraph?.();

      if (
        !createSupergraphResult?.errors &&
        createSupergraphResult?.data?.account?.createGraph?.__typename !==
          'GraphCreationError'
      ) {
        const graphId = createSupergraphResult?.data?.account?.createGraph?.id;
        // fire ga4 event
        trackCustomEvent({
          action: 'created_demo_graph',
          category: '6M2S: Onboarding',
          secondaryLabel: `schemaSource:INTRO`,
          graphId,
          graphVariant,
        });
        history.push({
          ...setOverlayVisibleLocation({
            location: {
              ...orgRoutes.graphs.location({ orgId }),
              state: {
                initiatingSupergraphState: {
                  graphId,
                  graphVariant,
                },
              },
            },
            overlayName: Config.modals.initiatingCloudEndpoint,
          }),
        });
      } else {
        throw new ApolloError({
          errorMessage:
            createSupergraphResult.data?.account?.createGraph?.__typename ===
            'GraphCreationError'
              ? createSupergraphResult.data.account.createGraph.message
              : 'Failed to create demo graph.',
        });
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  }, [createDemoSupergraph, graphVariant, history, orgId, trackCustomEvent]);

  const handleClick = async (event: React.MouseEvent<HTMLElement>) => {
    onClick?.(event);

    await createGraph();
  };

  if (!hasSchemaDocs) return null;

  return (
    <Button
      size={size}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={handleClick}
      isLoading={isProcessing}
      loadingText={btnLabel}
      isDisabled={isProcessing}
      {...rest}
    >
      {btnLabel}
    </Button>
  );
};
